import { CITATION_BRAND_COLORS } from '@/common/app/modules/media/citation'
import { CitationBrand, CitationCategoryChartRow } from '@/common/model/media/citation'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import HorizontalBarChart from '@/components/view/atoms/chart/HorizontalBarChart'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'

import GeoChartCard from '@/components/container/modules/mediaGeo/common/GeoChartCard'
import GeoSection from '@/components/container/modules/mediaGeo/common/GeoSection'

import CitationBrandLegend from './CitationBrandLegend'

interface Props {
  brands: CitationBrand[]
  categoryChart: CitationCategoryChartRow[]
  ownCount: number
  competitorCount: number
  selectedCategory: string
  onSelectCategory(category: string): void
}

// 言及元ジャンル（ジャンル別の言及件数比較と内訳テーブル）
const CitationGenreChart = ({
  brands,
  categoryChart,
  ownCount,
  competitorCount,
  selectedCategory,
  onSelectCategory
}: Props) => {
  const ownBrand = brands[0]
  // 自社の言及件数が多い順に並べ、その並び順を「自社順位」として示す
  const rows = [...categoryChart].sort(
    (a, b) => Number(b[ownBrand?.name] || 0) - Number(a[ownBrand?.name] || 0)
  )

  return (
    <GeoSection
      title='言及元ジャンル'
      subText='ブランドが言及されているジャンル'
      subNode={<CitationBrandLegend brands={brands} totals={[ownCount, competitorCount]} />}
    >
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
        <GeoChartCard title='ジャンル別 言及件数比較' height={Math.max(320, rows.length * 36)}>
          <HorizontalBarChart
            labels={rows.map((row) => row.category)}
            items={brands.map((brand, i) => ({
              title: brand.name,
              values: rows.map((row) => Number(row[brand.name] || 0)),
              color: CITATION_BRAND_COLORS[i % CITATION_BRAND_COLORS.length]
            }))}
          />
        </GeoChartCard>

        <table className='w-full border-collapse'>
          <thead>
            <TableRow head>
              <TableHeadItem type='thin' width={64}>
                <Text size={12} color={TextColor.SECONDARY}>
                  自社順位
                </Text>
              </TableHeadItem>
              <TableHeadItem type='thin'>
                <Text size={12} color={TextColor.SECONDARY}>
                  ジャンル
                </Text>
              </TableHeadItem>
              {brands.map((brand, i) => (
                <TableHeadItem key={i} type='thin' width={88}>
                  <Text size={12} color={TextColor.SECONDARY} truncate>
                    {brand.name}
                  </Text>
                </TableHeadItem>
              ))}
            </TableRow>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                <TableRowItem width={64} align='middle'>
                  <Text size={14}>{i + 1}</Text>
                </TableRowItem>
                <TableRowItem align='middle'>
                  <Badge
                    size={14}
                    badgeType={
                      selectedCategory === row.category
                        ? BadgeType.SQUARE_TERTIARY
                        : BadgeType.SQUARE_SECONDARY
                    }
                    onClick={() =>
                      onSelectCategory(selectedCategory === row.category ? '' : row.category)
                    }
                  >
                    {row.category}
                  </Badge>
                </TableRowItem>
                {brands.map((brand, bi) => (
                  <TableRowItem key={bi} width={88} align='middle'>
                    <Text size={14}>{Number(row[brand.name] || 0)}</Text>
                  </TableRowItem>
                ))}
              </TableRow>
            ))}
          </tbody>
        </table>
      </div>
    </GeoSection>
  )
}

export default CitationGenreChart
